import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import { managedBuildingNames, qualifications, values } from "../../data/general";
import { backgroundImg } from "src/assets/assetsExport";
import PropertymarkBanner from "../../components/PropertymarkBanner/PropertymarkBanner";

interface AboutUsPage2Props {}

export default function AboutUsPage2(props: AboutUsPage2Props): JSX.Element {
  const location = useLocation();
  const [expandedCode, setExpandedCode] = useState<string | null>(null);

  useEffect(() => {
    if (location.hash === "#regulatory-stack") {
      const el = document.getElementById("regulatory-stack");
      if (el) {
        const rect = el.getBoundingClientRect();
        const offset = 100; // account for header
        const top = rect.top + window.scrollY - offset;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }
  }, [location]);

  return (
    <>
      <Helmet>
        <title>About & Qualifications | Letting Engine</title>
        <meta
          name="description"
          content="Letting Engine operates at the intersection of property technology and human expertise, engineering the perfect rental experience."
        />
        <meta
          name="keywords"
          content="residential property, real estate, london, about us, qualifications, ARLA, Property Ombudsman, TDS, CMP"
        />
      </Helmet>

      {/* HERO: THE BLUEPRINT */}
      <section className="min-h-[90vh] pt-40 pb-20 w-full relative flex items-center justify-center">
        <img
          src={backgroundImg.social}
          alt="social events"
          className="absolute inset-0 w-full h-full object-cover opacity-15 filter grayscale contrast-125 pointer-events-none"
          style={{ zIndex: 0 }}
        />
        <div className="relative z-10 text-center max-w-4xl px-6">
          <h1 className="font-teko leading-none mb-8 tracking-[-2px] text-6xl">
            <span className="stroke-1 text-text-slate">About Us</span>
          </h1>
          <p className="font-manrope text-base leading-relaxed text-text-slate max-w-2xl mx-auto mb-6">
            Letting Engine is a premier residential property letting agency
            based in London, specializing in luxury rentals. With a prime
            location in the heart of the city, we offer an extensive portfolio
            of high-end properties in sought-after neighborhoods.
            <br />
            <br />
            Our dedicated team of experts is committed to delivering exceptional
            service, providing a seamless letting experience for both landlords
            and tenants.
          </p>
          <p className="font-manrope text-sm leading-relaxed text-text-slate max-w-2xl mx-auto mb-12 border-l-2 border-l-solid border-l-brand-yellow pl-4">
            Letting Engine is a trading name of New Vision Real Estate Ltd,
            registered in England and Wales (Company No. 12987204). All letting
            and property management services are provided by New Vision Real
            Estate Ltd.
          </p>
          <Link
            to="/about-us/team"
            className="inline-block font-teko text-lg uppercase text-text-obsidian border-b border-b-solid border-b-brand-yellow hover:text-brand-gold transition-colors mb-8"
          >
            Meet our team
          </Link>

          <div className="flex justify-center flex-col sm:flex-row gap-8 sm:gap-16  border-t border-t-solid border-t-[rgba(0,0,0,0.1)] pt-8 ">
            <div className="text-center">
              <h3 className="font-teko text-5xl text-brand-yellow">London</h3>
              <p className="font-manrope text-xs uppercase tracking-wider">
                Based in Prime Locations
              </p>
            </div>
            <div className="text-center">
              <h3 className="font-teko text-5xl text-brand-yellow">24/7</h3>
              <p className="font-manrope text-xs uppercase tracking-wider">
                Client Support
              </p>
            </div>
            <div className="text-center">
              <h3 className="font-teko text-5xl text-brand-yellow">
                Propertymark
              </h3>
              <p className="font-manrope text-xs uppercase tracking-wider">
                Accredited
              </p>
            </div>
          </div>
        </div>
      </section>

      <PropertymarkBanner />

      {/* 2. NARRATIVE (About Us) */}
      <section className="py-32 relative bg-bg-porcelain">
        <div className="max-w-[1440px] mx-auto px-[5%] grid grid-cols-1 md:grid-cols-2 gap-24 items-start ">
          <div className="flex flex-col">
            <h3 className="font-teko text-4xl mb-8">Built for Performance</h3>
            <p className="font-manrope text-lg leading-relaxed text-text-slate mb-8">
              Letting Engine was founded on the belief that luxury property
              deserves a smarter approach. We understand that outstanding
              results come from active asset management, not just placing
              properties on a listing.
            </p>
            <p className="font-manrope text-lg leading-relaxed text-text-slate mb-8">
              Today, we manage a curated portfolio of London’s finest
              residences. Our team is a blend of seasoned property brokers and
              data analysts, ensuring that our clients receive advice backed by
              hard numbers, not just instinct.
            </p>
            <ul className="list-none p-0 grid grid-cols-1 mobile:grid-cols-2 gap-4">
              <li className="flex items-center gap-2 font-manrope text-sm font-semibold uppercase">
                <span className="w-5 h-1 mr-2 bg-brand-yellow inline-block transform skew-x-[var(--skew)]"></span>{" "}
                Prime Central London Specialists
              </li>
              <li className="flex items-center gap-2 font-manrope text-sm font-semibold uppercase">
                <span className="w-5 h-1 mr-2 bg-brand-yellow inline-block transform skew-x-[var(--skew)]"></span>{" "}
                Dedicated Account Managers
              </li>
              <li className="flex items-center gap-2 font-manrope text-sm font-semibold uppercase">
                <span className="w-5 h-1 mr-2 bg-brand-yellow inline-block transform skew-x-[var(--skew)]"></span>{" "}
                24/7 Digital Dashboard
              </li>
              <li className="flex items-center gap-2 font-manrope text-sm font-semibold uppercase">
                <span className="w-5 h-1 mr-2 bg-brand-yellow inline-block transform skew-x-[var(--skew)]"></span>{" "}
                In-house Data Analytics Team
              </li>
            </ul>
          </div>
          <div className="h-[600px] relative clip-path-[polygon(10%_0,100%_0,100%_90%,90%_100%,0_100%)] md:order-[-1] lg:h-[400px]">
            <img
              src={backgroundImg.meeting2}
              alt="Office Architecture"
              className="w-full h-full object-cover filter transition-all duration-500 hover:filter-none"
            />
          </div>
        </div>
      </section>

      {/* BUILDINGS WE KNOW WELL */}
      <section className="py-24 px-[5%] relative bg-bg-white border-t border-t-solid border-t-[rgba(0,0,0,0.06)]">
        <div className="max-w-[1600px] mx-auto">
          <div className="mb-12 pl-8 border-l-4 border-l-solid border-l-brand-yellow">
            <span className="font-manrope text-xs uppercase tracking-widest text-text-slate block mb-2">
              Portfolio depth
            </span>
            <h2 className="page-section-heading">
              Buildings we know well
            </h2>
            <p className="font-manrope text-base text-text-slate max-w-2xl mt-6 leading-relaxed">
              From One Crown Place and Lincoln Square to established towers
              across prime and emerging London neighbourhoods — we know how
              these buildings let, what tenants expect, and how to position
              your home competitively.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {managedBuildingNames.map((name) => (
              <span
                key={name}
                className="font-manrope text-sm text-text-obsidian bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.08)] px-4 py-2"
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION VISION AND VALUES */}
      <section className="py-24 px-[5%] relative bg-bg-porcelain">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-24 items-start  lg:gap-16">
          {/* LEFT: MISSION */}
          <div className="relative">
            <div className="font-manrope text-xs uppercase tracking-widest text-brand-gold mb-4 flex items-center gap-2">
              <div className="w-8 h-0.5 bg-brand-yellow transform skew-x-[var(--skew)]"></div>
              01: Mission
            </div>
            <h2 className="page-section-heading mb-8">
              REMOVING FRICTION
              <br />
              FROM <span className="text-brand-yellow">LUXURY RENTALS</span>
              <br />
              THROUGH PRECISION.
            </h2>
            <p className="font-manrope text-base leading-relaxed text-text-slate max-w-[600px] border-l border-l-solid border-l-[#ddd] pl-8">
              Our mission is to connect discerning tenants with their perfect
              luxury homes while providing comprehensive services that meet the
              needs of landlords. We are committed to delivering a seamless
              letting process, offering an exclusive collection of high-end
              properties, and ensuring utmost satisfaction for both landlords
              and tenants. With our expertise, dedication, and personalized
              approach, we aim to make the letting experience exceptional and
              memorable.
            </p>
          </div>

          {/* LEFT: MISSION */}
          <div className="relative">
            <div className="font-manrope text-xs uppercase tracking-widest text-brand-gold mb-4 flex items-center gap-2">
              <div className="w-8 h-0.5 bg-brand-yellow transform skew-x-[var(--skew)]"></div>
              02: Vision
            </div>
            <h3 className="font-teko text-4xl mb-8">Vision</h3>
            <p className="font-manrope text-base leading-relaxed text-text-slate max-w-[600px] border-l border-l-solid border-l-[#ddd] pl-8">
              Our vision is to be the leading provider of luxury residential
              property letting services in London. We strive to set the highest
              standard in the industry, recognized for our exceptional
              properties, unparalleled customer service, and innovative
              approach. Our aim is to exceed the expectations of our clients,
              build long-lasting relationships, and be the go-to agency for
              luxury rentals in the city.
            </p>
          </div>

          {/* RIGHT: VALUES & VISION */}
          <div className="flex flex-col  gap-6 col-span-1 lg:col-span-2">
            <div className="font-manrope text-xs uppercase tracking-widest text-brand-gold mb-4 flex items-center gap-2">
              <div className="w-8 h-0.5 bg-brand-yellow transform skew-x-[var(--skew)]"></div>
              03: Operating Values
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="bg-bg-porcelain p-8 border border-solid border-[rgba(0,0,0,0.05)] transform  transition-all duration-300 ease-lux cursor-default relative hover:bg-text-obsidian hover:text-white hover:translate-x-[-20px] hover:shadow-[20px_20px_0px_rgba(0,0,0,0.1)] group"
                >
                  <div className="">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-teko text-3xl m-0">{value.header}</h3>
                    </div>
                    <p className="font-manrope text-sm text-text-slate leading-relaxed opacity-80 m-0 group-hover:text-[#ccc] group-hover:opacity-100">
                      {value.p}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 2. QUALIFICATIONS (Interactive Log) */}

      <section
        id="regulatory-stack"
        className="py-32 px-[5%] relative bg-[#111] text-white"
      >
        <div className="max-w-[1600px] mx-auto">
          <div className="mb-16 flex justify-between flex-col lg:flex-row items-start gap-4 lg:gap-0 lg:items-end border-b border-b-solid border-b-[rgba(255,255,255,0.1)] pb-8 ">
            <div>
              <span className="font-manrope text-xs uppercase tracking-widest text-brand-yellow block mb-2">
                Compliance
              </span>
              <h2 className="page-section-heading">
                Regulatory Stack
              </h2>
            </div>
            <p className="text-[#888] max-w-[400px] lg:text-right text-left">
              We maintain the highest level of accreditation in the industry.
              Our operations are audited and insured for total client security.
            </p>
          </div>

          <div className="flex flex-col gap-px">
            {qualifications.map((q, idx) => {
              const isExpanded = expandedCode === q.code;
              return (
              <div
                key={q.code}
                role="button"
                tabIndex={0}
                onClick={() => setExpandedCode(isExpanded ? null : q.code)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setExpandedCode(isExpanded ? null : q.code);
                  }
                }}
                className={`bg-[rgba(255,255,255,0.03)] border-l-2 border-l-solid overflow-hidden cursor-pointer transition-all duration-300 ${
                  isExpanded
                    ? "border-l-brand-yellow bg-[rgba(255,255,255,0.08)]"
                    : "border-l-transparent h-[180px] sm:h-[90px]"
                }`}
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between lg:px-12 sm:h-[90px] h-auto p-10 px-6 gap-4 sm:gap-0">
                  <span className="font-manrope text-brand-yellow text-xs uppercase tracking-widest w-[100px]">
                    {q.code}
                  </span>
                  <div
                    className={`font-teko text-3xl uppercase flex-1 lg:text-2xl flex items-start gap-3`}
                  >
                    <span>{q.name}</span>
                    {q.logo && (
                      <img
                        src={q.logo}
                        alt={q.name}
                        className="h-7 object-contain bg-white p-[0.2rem] rounded-sm"
                      />
                    )}
                  </div>
                  <span className={`font-manrope text-xs uppercase tracking-wide border border-solid px-4 py-1 transition-all duration-300 ${
                    isExpanded
                      ? "bg-brand-yellow text-black border-brand-yellow"
                      : "border-[rgba(255,255,255,0.2)] text-[#aaa]"
                  }`}>
                    {q.badge}
                  </span>
                </div>
                <div className={`px-12 pb-8 transition-all duration-300 lg:px-6 lg:flex-col lg:gap-8 ${
                  isExpanded
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-5 h-0 overflow-hidden pb-0"
                }`}>
                  <div className="flex md:gap-16 md:flex-row flex-col gap-8">
                    <p className="font-manrope text-base leading-relaxed text-[#ccc] flex-[2]">
                      {q.description.map((p: any, pIndex: number) => {
                        return (
                          <p
                            dangerouslySetInnerHTML={{ __html: p }}
                            key={pIndex}
                            className="mb-4"
                          />
                        );
                      })}
                      {q.link && (
                        <p>
                          Click&nbsp;
                          <a
                            href={q.link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-yellow"
                          >
                            here
                          </a>
                          &nbsp;to&nbsp;{q.link.text}.
                        </p>
                      )}
                    </p>
                    {q.details && (
                      <div className="flex-1 lg:border-l border-l-solid border-t lg:border-t-0 border-l-[rgba(255,255,255,0.1)] lg:pl-8 border-l-0 border-t-[rgba(255,255,255,0.1)] pt-4 pl-0">
                        {q.details.map((item: any, idx2: number) => (
                          <div
                            key={idx2}
                            className={
                              idx2 < q.details.length - 1 ? "mb-4" : ""
                            }
                          >
                            <span className="font-manrope text-xs text-[#666] uppercase block mb-1">
                              {item.label}
                            </span>
                            <span className="font-teko text-2xl block">
                              {item.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        </div>
      </section>
      <p className="font-manrope text-xs text-[#ccc]/30 text-center py-8 px-10 bg-[#161616]">
        Letting Engine is a business name used by New Vision Real Estate
        Limited, registered in England and Wales.
        <br />
        Company No.: 12987204. VAT Number: 386337268.
      </p>
    </>
  );
}
